import { Timestamp } from 'firebase/firestore';
import { SectionCard } from '../trainer/SectionCard';
import { Button } from '../ui';
import { formatInTimezone } from '@/shared/utils/timezone';
import type { WorkoutAssignment } from '../../types';

interface UpcomingWorkoutsProps {
  upcomingAssignments: WorkoutAssignment[];
  templateNames: Map<string, string>;
  hasAvailabilitySlots: boolean;
  startingId: string | null;
  timezone?: string;
  onBookSessions: () => void;
  onReschedule: (assignment: WorkoutAssignment) => void;
  onStartSession: (assignment: WorkoutAssignment) => void;
}

export function UpcomingWorkouts({
  upcomingAssignments,
  templateNames,
  hasAvailabilitySlots,
  startingId,
  timezone,
  onBookSessions,
  onReschedule,
  onStartSession,
}: UpcomingWorkoutsProps) {
  const getWorkoutName = (assignment: WorkoutAssignment) =>
    assignment.workoutTemplateName ||
    templateNames.get(assignment.workoutTemplateId) ||
    'Workout';

  return (
    <SectionCard
      eyebrow="Dashboard"
      title="Upcoming Workouts"
    >
      <div className="space-y-3">
        {upcomingAssignments.length === 0 && (
          <p className="text-gray-600 dark:text-slate-400 text-sm">
            No upcoming workouts scheduled yet.
          </p>
        )}
        {upcomingAssignments
          .sort(
            (a, b) =>
              (a.scheduledFor as Timestamp).toMillis() -
              (b.scheduledFor as Timestamp).toMillis()
          )
          .map((assignment) => {
            const assignmentDate = (assignment.scheduledFor as Timestamp).toDate();
            const now = new Date();
            const hoursUntil = (assignmentDate.getTime() - now.getTime()) / (1000 * 60 * 60);
            const canReschedule = hoursUntil >= 24;

            return (
              <div
                key={assignment.id}
                data-testid="workout-card"
                className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition"
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {getWorkoutName(assignment)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {formatInTimezone(assignmentDate, timezone)}
                    </p>
                    {!canReschedule && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Cannot reschedule - starts in less than 24 hours
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {canReschedule && (
                      <Button
                        onClick={() => onReschedule(assignment)}
                        variant="ghost"
                        size="sm"
                        className="flex-shrink-0"
                        title="Reschedule workout"
                        data-testid="reschedule-workout-button"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </Button>
                    )}
                    <Button
                      onClick={() => onStartSession(assignment)}
                      disabled={startingId === assignment.id}
                      isLoading={startingId === assignment.id}
                      size="sm"
                      className="flex-shrink-0"
                      data-testid="start-session-button"
                    >
                      Start
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        <Button
          variant="outline"
          size="sm"
          onClick={onBookSessions}
          className="w-full sm:w-auto"
          data-testid="book-sessions-button"
        >
          {hasAvailabilitySlots ? 'Update Schedule' : 'Book Sessions'}
        </Button>
      </div>
    </SectionCard>
  );
}
